import * as React from "react";
import { Loader } from "semantic-ui-react";
import { debounce, uniq, flatten, values } from "lodash";

import { AnsweredQuestionWithId } from "@teamprevent/common/model";

import { questionsForMedicalTypesSelector } from "../selectors";
import { MedicalTypeId, HealthSurveillanceQuestionConfig } from "../models";
import { SavingIndicator } from "../../components/SavingIndicator";
import { QuestionnaireState } from "../../questionnaires/model";
import * as questionIds from "../questions/questionIds";

export interface SubmittableHealthSurveillanceQuestionnaireProps {
    medicalTypes: MedicalTypeId[];
    questionnaire?: QuestionnaireState;
    saveQuestionnaire?: (questions: AnsweredQuestionWithId[], questionnaireKey?: string, eTag?: string) => Promise<void | string>;
    submitQuestionnaire: (questions: AnsweredQuestionWithId[], questionnaireKey: string) => Promise<boolean>;
    submitInvalidQuestionnaire: (questionnaireKey: string) => void;
    navigateToNextPage: () => void;
}

const saveQuestionnaire = async (
    props: SubmittableHealthSurveillanceQuestionnaireProps,
    setSaving: (val: boolean) => void,
    questions: AnsweredQuestionWithId[],
    questionnaireKey: string) => {

    setSaving(true);
    try {
        await props.saveQuestionnaire(questions, questionnaireKey, props.questionnaire?.eTag);
    } finally {
        setSaving(false);
    }
};

const saveDebounced = debounce(async (
    props: SubmittableHealthSurveillanceQuestionnaireProps,
    saving: boolean,
    setSaving: (val: boolean) => void,
    questions: AnsweredQuestionWithId[],
    questionnaireKey: string) => {
    if (!props.saveQuestionnaire || saving) {
        return;
    }
    await saveQuestionnaire(props, setSaving, questions, questionnaireKey);
}, 1500);

export const makeSubmittableHealthSurveillanceQuestionnaire =
<TProps extends object = undefined>(
        Component: React.FC<TProps>,
        questionnaireKey: string,
        questionConfigs: HealthSurveillanceQuestionConfig[]
    ): React.FC<SubmittableHealthSurveillanceQuestionnaireProps & TProps> =>
        (props: SubmittableHealthSurveillanceQuestionnaireProps & TProps) => {

            const trimAnswerAndUpdateEmployeeQuestion = (questionId: string, questions: AnsweredQuestionWithId[]) => {

                const question = questions.find(q => q.id === questionId);

                if (question) {
                    question.answer = question.answer?.toString()?.trim();
                }
            };

            const submitQuestionnaire = async (submittedQuestions: AnsweredQuestionWithId[]): Promise<boolean> => {
                saveDebounced.cancel();

                trimAnswerAndUpdateEmployeeQuestion(questionIds.Title, submittedQuestions);
                trimAnswerAndUpdateEmployeeQuestion(questionIds.FirstName, submittedQuestions);
                trimAnswerAndUpdateEmployeeQuestion(questionIds.LastName, submittedQuestions);

                if (props.saveQuestionnaire) {
                    await saveQuestionnaire(props, setSaving, submittedQuestions, questionnaireKey);
                }
                if (submittedQuestions.some(q => q.isValid !== true && q.isRemovedByUser !== true)) {
                    props.submitInvalidQuestionnaire(questionnaireKey);
                    return false;
                } else {
                    const submitted = await props.submitQuestionnaire(submittedQuestions, questionnaireKey);
                    props.navigateToNextPage();
                    return submitted;
                }
            };
            const [saving, setSaving] = React.useState(false);
            const [toggleTriggerSave, setToggleTriggerSave] = React.useState(false);
            const questions = React.useMemo(() =>
                props.questionnaire ?
                    uniq(flatten(props.questionnaire.sections.map(section => values(section.questions)))) :
                    [],
            [props.questionnaire]);

            React.useEffect(() => {
                if (saving) {
                    // Defer the call to saveQuestionnaire if already saving
                    setTimeout(() => setToggleTriggerSave(!toggleTriggerSave), 3000);
                }
                else {
                    saveDebounced(props, saving, setSaving, questions, questionnaireKey);
                }

                return saveDebounced.cancel;
            }, [props.questionnaire?.sections]);

            React.useEffect(() => {
                if (!saving) {
                    saveDebounced(props, saving, setSaving, questions, questionnaireKey);
                }
            }, [toggleTriggerSave]);

            if (!props.medicalTypes) {
                return <Loader active />;
            }

            const questionConfigsForMedicalTypes = questionsForMedicalTypesSelector(questionConfigs, props.medicalTypes);

            return (
                <>
                    <Component
                        {...props}
                        questionConfigs={questionConfigsForMedicalTypes}
                        readonly={false}
                        submitQuestionnaire={submitQuestionnaire}
                        task={null}
                    />
                    <SavingIndicator display={saving} />
                </>
            );
        };
